import { useCallback, useMemo, useState } from 'react';
import { useAssistantFacade } from './useAssistantFacade';
import { debounce } from 'lodash';
import { IAutocompleteSuggestions } from '../../model/AutocompleteSuggestions';
import { useUiManager } from './useUiManager';
import { IImage } from '../../../../infra';

const SUGGESTIONS_SOURCE = 'suggestions';

export function useAssistant() {
    const [suggestions, setSuggestions] = useState<IAutocompleteSuggestions | undefined>(undefined);
    const facade = useAssistantFacade();
    const uiManager = useUiManager();

    const getSuggestions = useCallback(
        async (term: string) => {
            let result: IAutocompleteSuggestions | undefined = undefined;
            if (term) {
                result = await facade.getSuggestions(
                    term,
                    suggestions?.session.get(),
                    SUGGESTIONS_SOURCE,
                );
            } else {
                suggestions?.clearSession();
            }
            setSuggestions(result);
        },
        [facade, suggestions],
    );

    const debouncedFn = useMemo(
        () =>
            debounce((term: string) => {
                getSuggestions(term);
            }, 300),
        [getSuggestions],
    );

    const getSuggestionsWithDebounce = useCallback(
        (term: string) => {
            debouncedFn(term);
        },
        [debouncedFn],
    );

    const createConversation = useCallback(
        async (userMessage: string, image: IImage | undefined) => {
            const conversation = await facade.createConversation(userMessage, image);
            uiManager.openConversation(conversation.getId());
        },
        [facade, uiManager],
    );

    const deleteConversation = useCallback(
        async (conversationId: string) => {
            return await facade.deleteConversation(conversationId);
        },
        [facade],
    );

    return {
        getSuggestionsWithDebounce,
        getSuggestions,
        suggestions,
        createConversation,
        deleteConversation,
    };
}
